import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
}

export default function Home() {
    const { t } = useTranslation()
    const bullets = t('home.obstacles', { returnObjects: true })

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="min-h-screen"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head>;
                o main.jsx remove os fallbacks estáticos (data-fallback) antes do
                primeiro render */}
            <title>{t('home.metaTitle')}</title>
            <meta name="description" content={t('home.metaDescription')} />
            <link rel="canonical" href={pageUrl()} />
            {/* Hero */}
            <section
                className="relative overflow-hidden py-24 lg:py-36 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="max-w-3xl"
                    >
                        <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">
                            {t('home.title')}
                            <span className="block text-2xl lg:text-3xl font-light text-white/50 mt-2">{t('home.subtitle')}</span>
                        </h1>
                        <p className="mt-5 text-base lg:text-lg text-slate-200 leading-relaxed">
                            {t('home.lead')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Obstacles + Circumstances — single compact section */}
            <section className="py-10 lg:py-14 bg-white">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="max-w-5xl mx-auto"
                    >
                        <p className="text-base text-slate-600 leading-relaxed mb-6">
                            {t('home.obstaclesIntro')}
                        </p>

                        {/* Compact 2-column numbered list */}
                        <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-10 list-none p-0 m-0">
                            {bullets.map((text, i) => (
                                <motion.li
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className="flex gap-3 items-start"
                                >
                                    <span aria-hidden="true" className="shrink-0 w-6 h-6 mt-0.5 rounded-full bg-brand-600 text-white text-xs font-bold flex items-center justify-center">
                                        {i + 1}
                                    </span>
                                    <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
                                </motion.li>
                            ))}
                        </ol>

                        {/* Circumstances strip */}
                        <div className="rounded-2xl bg-slate-900 p-6 lg:p-8">
                            <h2 className="text-base lg:text-lg font-bold text-white mb-5 leading-snug">
                                {t('home.circumstancesTitle')}
                            </h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="bg-white/10 border border-white/15 rounded-xl p-4">
                                    <h3 className="text-sm font-bold text-brand-300 mb-1.5 uppercase tracking-wide">{t('home.migrants.title')}</h3>
                                    <p className="text-sm text-slate-300 leading-relaxed">
                                        {t('home.migrants.text')}
                                    </p>
                                </div>
                                <div className="bg-white/10 border border-white/15 rounded-xl p-4">
                                    <h3 className="text-sm font-bold text-brand-300 mb-1.5 uppercase tracking-wide">{t('home.tourists.title')}</h3>
                                    <p className="text-sm text-slate-300 leading-relaxed">
                                        {t('home.tourists.text')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Closing */}
            <section className="py-8 lg:py-10 bg-slate-50 border-t border-slate-100">
                <div className="container-custom">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="max-w-5xl mx-auto space-y-4"
                    >
                        <p className="text-sm lg:text-base text-slate-700 leading-relaxed border-s-4 border-brand-500 ps-5">
                            {t('home.closing1')}
                        </p>
                        <p className="text-sm lg:text-base text-slate-700 leading-relaxed">
                            {t('home.closing2')}
                        </p>
                        <a
                            href="https://infovitimas.pt/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-brand-600 font-bold hover:underline"
                        >
                            {t('home.infovitimasLink')} <ExternalLink size={14} aria-hidden="true" />
                        </a>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
